/**
 * @file OAuthButtons.jsx — Google + Apple sign-in buttons.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { authService } from "../services/auth";
import { AUTH_STRINGS as S } from "../constants/strings";
import "./OAuthButtons.css";

const shouldUseRedirectFlow = () =>
  /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

function GoogleButtonWrapper({ onSuccess, onError }) {
  const containerRef = useRef(null);
  const [btnWidth, setBtnWidth] = useState(400);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) setBtnWidth(Math.min(w, 400));
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="oauth-btn-wrap">
      <button type="button" className="oauth-btn oauth-btn-google">
        <GoogleIcon />
        <span>{S.OAUTH_GOOGLE}</span>
      </button>
      <div className="oauth-google-iframe">
        <GoogleLogin
          onSuccess={onSuccess}
          onError={onError}
          text="continue_with"
          locale="es"
          shape="rectangular"
          size="large"
          width={btnWidth}
        />
      </div>
    </div>
  );
}

const loadAppleSDK = (() => {
  let promise = null;
  return () => {
    if (promise) return promise;
    promise = new Promise((resolve, reject) => {
      if (window.AppleID) return resolve();
      const script = document.createElement("script");
      script.src =
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Apple SDK"));
      document.head.appendChild(script);
    });
    return promise;
  };
})();

export default function OAuthButtons({ role = null, onSuccess, onError, onNewGoogleUser, referralCode, acceptTerms = false }) {
  const [appleReady, setAppleReady] = useState(false);
  const useRedirect = useState(() => shouldUseRedirectFlow())[0];

  useEffect(() => {
    if (!import.meta.env.VITE_APPLE_SERVICE_ID) return;
    loadAppleSDK()
      .then(() => {
        window.AppleID.auth.init({
          clientId: import.meta.env.VITE_APPLE_SERVICE_ID,
          scope: "name email",
          redirectURI: window.location.origin,
          usePopup: true,
        });
        setAppleReady(true);
      })
      .catch(() => {});
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await authService.googleLogin(credentialResponse.credential, role, referralCode, acceptTerms);
      if (res.data?.isNewUser && onNewGoogleUser) {
        onNewGoogleUser({ credential: credentialResponse.credential, ...res.data });
      } else {
        onSuccess(res.data);
      }
    } catch (err) {
      onError(err.response?.data?.message || S.OAUTH_ERROR_GOOGLE);
    }
  };

  const handleAppleClick = useCallback(async () => {
    try {
      const res = await window.AppleID.auth.signIn();
      const { id_token } = res.authorization;
      const firstName = res.user?.name?.firstName || "";
      const lastName = res.user?.name?.lastName || "";
      const apiRes = await authService.appleLogin(id_token, role, firstName, lastName, referralCode, acceptTerms);
      onSuccess(apiRes.data);
    } catch (err) {
      if (err?.error === "popup_closed_by_user") return;
      onError(err.response?.data?.message || S.OAUTH_ERROR_APPLE);
    }
  }, [role, onSuccess, onError, referralCode, acceptTerms]);

  const handleGoogleRedirect = () => {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (referralCode) params.set("referralCode", referralCode);
    if (acceptTerms) params.set("acceptTerms", "1");
    const pendingPlan = localStorage.getItem("pendingPlan");
    if (pendingPlan) params.set("plan", pendingPlan);
    const qs = params.toString();
    window.location.href = `/api/v1/auth/google/redirect${qs ? `?${qs}` : ""}`;
  };

  return (
    <div className="oauth-list">
      {useRedirect ? (
        <button type="button" className="oauth-btn oauth-btn-google" onClick={handleGoogleRedirect}>
          <GoogleIcon />
          <span>{S.OAUTH_GOOGLE}</span>
        </button>
      ) : (
        <GoogleButtonWrapper onSuccess={handleGoogleSuccess} onError={() => onError(S.OAUTH_ERROR_GOOGLE)} />
      )}
      {appleReady && (
        <button type="button" className="oauth-btn oauth-btn-apple" onClick={handleAppleClick}>
          <AppleIcon />
          <span>{S.OAUTH_APPLE}</span>
        </button>
      )}
    </div>
  );
}
